import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { darken, useCtaPress } from './shared';
import type { TemplateProps } from './types';

const DEFAULT_COLOR = '#00BCD4';

export function DownloadTemplate({
  state,
  onAccept,
  appName,
  logoUrl,
  buttonColor,
  buttonText,
  buttonSubtitle,
}: TemplateProps) {
  const { t } = useTranslation();
  const ready = state.status === 'awaiting-click';
  const { pressed, onClick } = useCtaPress(onAccept);
  const color = buttonColor?.trim() || DEFAULT_COLOR;
  const customText = buttonText?.trim() || '';
  const customSubtitle = buttonSubtitle?.trim() || '';

  const initial = (appName || t('download.fallbackAppName')).trim().charAt(0).toUpperCase();

  return (
    <div className="min-h-dvh select-none" style={{ backgroundColor: '#f3f4f6' }}>
      <div className="flex min-h-dvh flex-col items-center justify-center px-8 pb-16">
        {/* Logo */}
        <div className="mb-5 h-24 w-24 overflow-hidden rounded-[22px] bg-white shadow-lg ring-1 ring-black/8">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={appName || t('download.fallbackAppName')}
              className="h-full w-full object-cover"
            />
          ) : (
            <div
              className="flex h-full w-full items-center justify-center text-3xl font-bold text-white"
              style={{ backgroundColor: color }}
            >
              {initial}
            </div>
          )}
        </div>

        {/* App name */}
        <h1 className="mb-8 text-center text-2xl font-bold tracking-tight text-zinc-800">
          {appName || t('download.fallbackAppName')}
        </h1>

        {/* CTA button */}
        <button
          type="button"
          onClick={onClick}
          className={`w-full max-w-xs rounded-md py-4 text-sm font-bold text-white transition-all duration-200 ${ready && customText ? 'px-4 tracking-normal [overflow-wrap:anywhere]' : 'uppercase tracking-widest'}`}
          style={
            ready
              ? {
                  backgroundColor: pressed ? darken(color) : color,
                  transform: pressed ? 'scale(0.98)' : 'scale(1)',
                }
              : { backgroundColor: '#d1d5db', cursor: 'default' }
          }
          disabled={!ready}
        >
          {ready ? (
            customText || t('download.cta')
          ) : (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('download.checking')}
            </span>
          )}
        </button>

        {/* Subtitle */}
        {ready && (
          <p
            className={`mt-4 text-center text-sm text-zinc-400${customSubtitle ? ' max-w-full [overflow-wrap:anywhere]' : ''}`}
          >
            {customSubtitle || t('download.subtitle')}
          </p>
        )}
      </div>
    </div>
  );
}
